import { FileDoneOutlined } from '@ant-design/icons';
import {
  Button,
  FormInstance,
  Popconfirm,
  Row,
} from 'antd';

import { DeleteAction, DisableAction, EditAction } from 'components';

import { IInitialTableActions, InitialTableActions } from 'types';

import {
  handleApproveRow,
  handleDeleteRow,
  handleEditRow,
  handleToggleIsActiveRow,
} from './lib';

interface ICRUDTableInitialActions<T> {
  form?: FormInstance<T>;
  record: T;
  disabled: boolean;
  initialTableActions?: IInitialTableActions[];
  setEditingKey?: (key?: number) => void;
  onPatchTableData?: (data: T, actionType?: InitialTableActions) => void;
  onDeleteTableData?: (id: number) => void;
}

export const CRUDTableInitialActions = <T extends Record<string, unknown>, >({
  form,
  record,
  disabled,
  initialTableActions,
  setEditingKey,
  onPatchTableData,
  onDeleteTableData,
}: ICRUDTableInitialActions<T>) => {
  const enableAction = initialTableActions?.find((action) => action.type === InitialTableActions.Toggle);

  return (
    <Row wrap={false} justify={enableAction ? 'space-between' : 'start'} align='middle'>
      {initialTableActions?.map((action) => {
        switch (action.type) {
          case InitialTableActions.Edit:
            return (
              <EditAction
                key={action.type}
                title='Edit row'
                disabled={disabled}
                onClick={handleEditRow({ form, record, setEditingKey })}
              />
            );
          case InitialTableActions.Delete:
            return (
              <DeleteAction
                key={action.type}
                disabled={disabled}
                onConfirm={handleDeleteRow({ record, onDeleteTableData })}
              />
            );
          case InitialTableActions.Approve:
            return (
              <Popconfirm
                key={action.type}
                title='All data is correct?'
                disabled={disabled}
                onConfirm={handleApproveRow({ record, onPatchTableData })}
              >
                <Button
                  type='text'
                  size='small'
                  className='mr-1'
                  disabled={disabled}
                  icon={(
                    <FileDoneOutlined
                      title='Approve'
                      style={{ color: disabled ? '#bfbfbf' : '#52c41a' }}
                    />
                  )}
                />
              </Popconfirm>
            );
          default:
            return null;
        }
      })}

      {enableAction && (
        <DisableAction
          disabled={disabled}
          checked={!!record.isActive}
          title={record.isActive ? `Disable ${enableAction.title || 'row'}?` : `Enable ${enableAction.title || 'row'}?`}
          onConfirm={handleToggleIsActiveRow({ record, onPatchTableData })}
        />
      )}
    </Row>
  );
};
